/**
 * MSG-7: страница E2E-чата владельца с сотрудником (зеркало экрана MSG-6).
 * Маршрут /msg/:userId (userId = users.user_id, как в /employee/:id).
 * QR-1b: та же страница для участника по приглашению — маршрут
 * /msg/m/:memberId (msg_members.id): ensure телом {member_id}, имя визави —
 * снимок из ответа ensure (peer_name), не из /website/employees; гейт —
 * messengerV2 И msgInvites из /me. Криптослой/контроллер общие.
 *
 * Правила страницы:
 * - гейт: messengerV2 из /me (критерий 7 — флаг off => страницы нет);
 * - лидерство Web Locks ДО подъёма крипты (критерий 4): вторая вкладка
 *   честно говорит, что чат открыт в другой, и НЕ трогает IndexedDB;
 * - криптослой тянется лениво внутри контроллера (WASM-чанк только здесь);
 * - «Повторить» — только у восстановимых отказов (урок MSG-6 №5).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CabinetShell from '../../components/UI/CabinetShell/cabinetShell';
import { AttachmentDraft, MsgAttachments, UploadingNote } from '../../msg/attachmentsUi';
import { replySnapshot } from '../../msg/replyTo';
import {
  indexMessages, ReplyButton, ReplyDraftBar, ReplyQuote, useQuoteJump,
} from '../../msg/replyToUi';
import PinnedWarnings, { SendBlocked } from '../../msg/pinnedWarnings';
import SendButton from '../../msg/sendButton';
import SecurityPanel, { SecurityLock, unverifiedCount } from '../../msg/securityPanel';
import { useAuth } from '../../context/AuthContext';
import { MsgApi } from '../../msg/msgApi';
import { browserDeviceId, rotateBrowserDeviceId } from '../../msg/deviceId';
import {
  askYield, canAskYield, onYieldRequest, requestLeadership, setLocalHandoff,
  TAKEOVER_WAIT_MS, waitLocalHandoff,
} from '../../msg/leader';
import { ChatController, PEER_WAIT_RETRY_MS, storeNameFor } from '../../msg/chatController';
import { forgetDeviceStore } from '../../msg/deviceTakeover';
import axiosInstance from '../../utils/router/axiosInstance';
import { logger } from '../../utils/logger';
import './msgChat.css';

/**
 * Передача лидерства ВНУТРИ вкладки (ревью QR-1b р.3, High) переехала в
 * `msg/leader` (QR-2 В2): страниц msg-машины стало ДВЕ (чат и комната), а
 * замок один на origin — значит и эстафета обязана быть одна. Модульная
 * переменная внутри страницы вернула бы тот же баг на переходе
 * «личный чат -> комната». Реэкспорт оставлен: на него ссылаются тесты.
 */
export { waitLocalHandoff } from '../../msg/leader';

const trustText = (who) => ({
  first_use: `Первый контакт с этим устройством ${who}. Сверьте код безопасности при случае.`,
  key_changed: `Ключ устройства ${who} СМЕНИЛСЯ (например, переустановка приложения). Сверьте код безопасности заново!`,
});

/**
 * Обёртка маршрута (ревью QR-1b р.1, High): переход в той же вкладке
 * /msg/5 -> /msg/m/7 переиспользовал бы компонент — снимок чата, blocked,
 * текст в поле ввода и ФИО прошлого визави жили бы до первого update нового
 * контроллера (отправка могла уйти новому peer при старом UI). key по визави
 * = честный remount: весь state с нуля, cleanup прежнего эффекта (stop
 * контроллера, release лидерства) отрабатывает штатно.
 */
const MsgChat = () => {
  const { userId, memberId } = useParams();
  return <MsgChatPeer key={memberId !== undefined ? `m${memberId}` : `u${userId}`} />;
};

const MsgChatPeer = () => {
  const { userId, memberId } = useParams();
  const { id: ownerId, messengerV2, msgInvites, loaded } = useAuth();
  // QR-1b: визави — участник (/msg/m/:memberId) или сотрудник (/msg/:userId)
  const isMember = memberId !== undefined;
  const chatEnabled = messengerV2 && (!isMember || msgInvites);
  const TRUST_TEXT = trustText(isMember ? 'участника' : 'сотрудника');
  const [chat, setChat] = useState(null); // снимок state контроллера
  const [blocked, setBlocked] = useState(false);
  const [peerFio, setPeerFio] = useState(null);
  const [input, setInput] = useState('');
  // ATT С2: выбранный файл-черновик. Один на сообщение (v1) — скрепка
  // выбирает следующий только после отправки или ✕.
  const [file, setFile] = useState(null);
  // WEB-16: на какое сообщение отвечаем. Снимок, а не ссылка на строку:
  // уедет он, и человек обязан видеть именно то, что увидит собеседник.
  const [replyTo, setReplyTo] = useState(null);
  // Оригиналы для цитат — по ЭТОЙ переписке. Карта, а не поиск по списку
  // на каждое сообщение: лента бывает длинной, а цитат в ней много.
  const byId = indexMessages(chat?.messages);
  const fileRef = useRef(null);
  // WEB-31: фокус возвращается в поле после отправки кнопкой
  const inputRef = useRef(null);
  // Визави ещё не поднимал крипту (см. bootstrapError): ждём его и
  // перезапускаем подъём сами. Для владельца это ровно то же ожидание, что и
  // у участника: устройство собеседника заводится при открытии ЧАТА.
  const [retryTick, setRetryTick] = useState(0);
  // Д-20: разрешение вытеснить ЧУЖОЕ устройство. Ref, а не state, и
  // ОДНОРАЗОВОЕ: клик разрешает ровно один перенос, а не режим «можно
  // всегда». Состоянием в зависимостях эффекта оно устроило бы цикл
  // перезапусков, а сброс по phase==='ready' — лишний перезапуск.
  const takeDeviceRef = useRef(false);
  // Второй тап по «Перенести сюда», пока поднимается новый контроллер.
  const takingDeviceRef = useRef(false);
  // Перенос чата в эту вкладку (с.64). takeTick — перезапуск эффекта,
  // yielded — «мы отдали чат соседу по его просьбе».
  const [takeTick, setTakeTick] = useState(0);
  const [yielded, setYielded] = useState(false);
  // 🔴 Ожидание переноса — НОМЕР попытки, а не булев флаг (ревью Codex р.1,
  // High-1): заход эффекта гасит спиннер, только если тот принадлежит ЕМУ.
  // Булев флаг оставлял «Переносим чат сюда…» навсегда, когда заход отменили
  // до ответа менеджера локов (уступка, автоповтор, смена визави).
  const [takingTick, setTakingTick] = useState(0);
  const taking = takingTick > 0;
  // Отказ ПОСЛЕ явной просьбы — «сосед не ответил», а не «просто занято».
  // Флаг гасится вместе с попыткой: иначе (ревью Codex р.1, Low) любой
  // следующий обычный отказ до конца жизни страницы врал бы про перенос.
  const [takeFailed, setTakeFailed] = useState(false);
  const takingTickRef = useRef(0); // тот же номер для чтения ВНУТРИ эффекта
  // 🔴 Собеседника УДАЛИЛИ (410 peer_gone) — переписка мертва терминально, и
  // держать на ней лидерство вкладки нельзя: 25.08 именно такая вкладка не
  // пустила владельца ни в один живой чат.
  //
  // Флаг булев и сбрасывается ТОЛЬКО remount'ом обёртки MsgChat (key по
  // визави). Скоупить приговор ключом визави прямо здесь бесполезно (ревью
  // Codex р.1, Medium): снимок `chat` от ПРЕЖНЕГО собеседника переживает смену
  // маршрута, и сравнение с текущим `peerKey` всё равно приговорило бы нового.
  // Настоящая защита — тот самый remount, и он не на честном слове: его
  // держит пин «переход /msg/5 -> /msg/m/7 = remount, старый чат не протекает»
  // (msgChat.member.test.jsx). Уберут key — покраснеет он, а не этот экран.
  const [gone, setGone] = useState(false);
  // WEB-23: панель «Защита переписки». Закрыта по умолчанию — она заменила
  // собой справку, которая раньше висела на экране всегда.
  const [securityOpen, setSecurityOpen] = useState(false);
  // 🔴 Фокус обязан ВЕРНУТЬСЯ на замок при закрытии (ревью 03.09): иначе он
  // падает на `body`, и клавиатурой до замка надо добираться табом с начала
  // страницы. Тот же приём, что у `returnFocus` в комнате и у формы подписи.
  const securityLockRef = useRef(null);
  const closeSecurity = () => {
    setSecurityOpen(false);
    securityLockRef.current?.focus();
  };
  // Своё устройство (Д-20 §3) спрашиваем ТОЛЬКО когда панель открыли: подъём
  // чата и так зовёт эту ручку раз в 5 с в режиме ожидания собеседника, и
  // вешать ещё один запрос на каждый заход в чат незачем.
  const [myDevice, setMyDevice] = useState(null);
  const controllerRef = useRef(null);
  const listRef = useRef(null);
  // WEB-16 срез 4: переход к оригиналу по нажатию на цитату.
  const { jumpTo, highlighted } = useQuoteJump(listRef);
  // 🔴 Уступает ТОЛЬКО держатель: заблокированная вкладка, услышав чужую
  // просьбу, не должна показывать «чат перенесён» — она его и не держала.
  const heldRef = useRef(false);

  // Сосед просит отдать лидерство. Отдаём штатным путём: флаг гасит эффект,
  // его cleanup останавливает контроллер и лишь затем отпускает лок.
  useEffect(() => onYieldRequest(() => {
    if (heldRef.current) setYielded(true);
  }), []);

  // Приговор ставим в отдельном эффекте, а не прямо в onUpdate контроллера:
  // так лидерство снимает ШТАТНЫЙ cleanup основного эффекта (stop() -> release),
  // тем же путём, что уступка соседу, — а не вторая точка отпускания лока.
  useEffect(() => {
    if (chat?.error?.kind === 'peer_gone') setGone(true);
  }, [chat?.error?.kind]);

  useEffect(() => {
    if (!loaded || !chatEnabled || !ownerId || yielded || gone) return undefined;
    // Номер попытки переноса, которой принадлежит ЭТОТ заход. Считается до
    // первого await: дальше ref уже могли обнулить.
    const myTick = takeTick;
    const isTakeover = myTick > 0 && takingTickRef.current === myTick;
    const doneTaking = () => {
      if (takingTickRef.current === myTick) takingTickRef.current = 0;
      setTakingTick((cur) => (cur === myTick ? 0 : cur));
    };
    let leader = null;
    let leaderP = null; // сам запрос лидерства — может висеть в момент ухода
    let controller = null;
    let cancelled = false;
    (async () => {
      await waitLocalHandoff(); // прежний контроллер этой вкладки отпустил лок
      if (cancelled) return;
      // После просьбы уступить лок ждём В ОЧЕРЕДИ: держатель отдаёт его не
      // мгновенно (stop() дожидается начатого шага машины). Дальше эта вкладка
      // так и остаётся «терпеливой» — сама она лок отпускает через localHandoff,
      // и очередь для неё пуста, а если его успел занять сосед, честнее
      // подождать, чем мгновенно отказать после явного действия человека.
      leaderP = requestLeadership(myTick > 0 ? { waitMs: TAKEOVER_WAIT_MS } : {});
      leader = await leaderP;
      // 🔴 Спиннер гасится на ОБОИХ путях, включая отменённый: иначе экран
      // «Переносим чат сюда…» переживал бы собственный заход (High-1).
      doneTaking();
      if (cancelled) { leader.release(); return; }
      if (!leader.granted) { setBlocked(true); setTakeFailed(isTakeover); return; }
      heldRef.current = true;
      const did = browserDeviceId();
      const allowTakeover = takeDeviceRef.current;
      takeDeviceRef.current = false;
      // Новый контроллер пошёл — кнопка исчезла, guard второго тапа снят.
      takingDeviceRef.current = false;
      controller = new ChatController({
        api: new MsgApi(did), ownerId, did, onUpdate: setChat, allowTakeover,
      });
      controllerRef.current = controller;
      controller.start(
        isMember
          ? { type: 'member', id: Number(memberId) }
          : { type: 'employee', id: Number(userId) },
      );
    })();
    return () => {
      cancelled = true;
      controllerRef.current = null;
      // Держателем быть перестали ровно здесь: лок ещё снимается (ниже), но
      // отвечать на чужую просьбу уступить эта вкладка больше не вправе.
      heldRef.current = false;
      const c = controller;
      const l = leader;
      const lp = leaderP;
      if (c) {
        // Лидерство (Web Lock) отпускаем ТОЛЬКО когда старый контроллер реально
        // остановился (ревью QR-1b р.2, High): его bootstrap/догон асинхронны,
        // а следующий контроллер (другой визави в той же вкладке или вкладка-
        // сосед) откроет ТОТ ЖЕ IndexedDB-стор — две машины на одном сторе
        // портят Olm-состояние. stop() никогда не реджектится; release()
        // возвращает «лок фактически снят» — следующий mount этой вкладки
        // (localHandoff) просит лок только после этого.
        setLocalHandoff(c.stop().then(() => l?.release()).catch(() => undefined));
        return;
      }
      // Контроллера нет, но запрос лидерства мог ещё ВИСЕТЬ (ушли со страницы
      // в первые миллисекунды, ревью р.4 lifecycle-пин): менеджер выдаст лок
      // уже «мёртвому» эффекту, а новый mount, спросив ifAvailable между выдачей
      // и отпусканием, получил бы отказ навсегда. Поэтому handoff = дождаться
      // выдачи и отпустить (release идемпотентен — async-хвост тоже отпустит).
      if (lp) setLocalHandoff(lp.then((got) => got.release()).catch(() => undefined));
      else l?.release();
    };
  }, [loaded, chatEnabled, ownerId, isMember, userId, memberId, retryTick,
    takeTick, yielded, gone]);

  // Автоповтор ожидания визави — только на peer_no_device (прочие отказы
  // подъёма повтором не лечатся). Перезапуск идёт штатным путём эффекта:
  // cleanup останавливает контроллер и кладёт localHandoff, следующий заход
  // его дожидается — лидерство и стор не пересекаются.
  // blocked сбрасываем: он мог остаться от захода, которому лок не достался,
  // и без сброса экран «чат открыт в другой вкладке» пережил бы удачный повтор.
  const retryNow = () => {
    setChat(null); setBlocked(false); setTakeFailed(false);
    setRetryTick((n) => n + 1);
  };

  /**
   * Д-20: «Перенести сюда» — единственный способ вытеснить другое устройство.
   *
   * 🔴 Сначала РОТИРУЕМ did, и только потом перезапускаем подъём. Прежний did
   * этого браузера мог быть уже ревокнут (человек открыл чат на телефоне —
   * наш стал недействителен), а ревокнутый did выжжен на сервере навсегда:
   * попытка зарегистрироваться им даёт 403, и кнопка бы просто не работала.
   *
   * 🔴 Второе нажатие игнорируем (ревью 03.09): между кликом и первым
   * снимком нового контроллера лежат stop() прежнего и ожидание эстафеты —
   * секунды, в течение которых кнопка ещё на экране. Двойной тап на телефоне
   * дал бы ДВЕ ротации, и контроллер, вышедший с did-2, ревокнул бы уже
   * записанный did-3 — человек получил бы 403 и ещё один круг.
   */
  const takeDevice = () => {
    if (takingDeviceRef.current) return;
    takingDeviceRef.current = true;
    const old = browserDeviceId();
    rotateBrowserDeviceId();
    // Старый стор больше не нужен никому: did выжжен либо не регистрировался.
    forgetDeviceStore(storeNameFor({ type: 'owner', id: ownerId }, old));
    // 🔴 WEB-23: снимок своего устройства протух в эту же секунду — did другой
    // (ревью 03.09, High). Без сброса панель показывала бы дату и обещание
    // «ключи хранятся только здесь» про устройство ДО переноса.
    setMyDevice(null);
    takeDeviceRef.current = true;
    retryNow();
  };

  /**
   * «Открыть чат здесь» — единственный выход на телефоне, где вкладку-держателя
   * не найти и не закрыть (с.64: из-за этого 25.08 владелец не вошёл ни в одну
   * комнату). Просим держателя уступить и встаём за локом в очередь; он отдаст
   * его штатно, остановив свой контроллер.
   *
   * 🔴 Повторный тап во время переноса игнорируем (ревью Codex р.1, High-2):
   * иначе два подряд заводили бы два запроса лидерства и лишний перезапуск
   * эффекта. Кнопка ещё и disabled — но guard дешевле, чем доверять рендеру.
   */
  const takeover = () => {
    if (taking) return;
    const next = takeTick + 1;
    askYield();
    setChat(null); setBlocked(false); setYielded(false); setTakeFailed(false);
    takingTickRef.current = next;
    setTakingTick(next);
    setTakeTick(next);
  };
  useEffect(() => {
    if (chat?.error?.kind !== 'peer_no_device') return undefined;
    const t = setTimeout(retryNow, PEER_WAIT_RETRY_MS);
    return () => clearTimeout(t);
    // 🔴 Зависимость — именно kind, а не chat: она половина защиты.
    // Расширить её до [chat] значило бы перезаводить таймер на каждый
    // снимок состояния, и тогда ожидание пошло бы на ЛЮБОМ отказе
    // подъёма (пин «автоповтор только на ожидании визави» это ловит).
  }, [chat?.error?.kind]);

  useEffect(() => {
    // Имя сотрудника — из готовой ручки списка; чату оно не обязательно.
    // У участника (QR-1b) имя приходит снимком из ensure (chat.peer.name) —
    // список сотрудников не дёргаем.
    if (isMember) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get('website/employees');
        const emp = data.find((e) => String(e.user_id) === String(userId));
        if (emp && !cancelled) setPeerFio(emp.fio);
      } catch (e) {
        logger.debug('Список сотрудников для шапки чата не загрузился', e);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isMember]);

  useEffect(() => {
    // автопрокрутка к свежему сообщению
    listRef.current?.scrollTo(0, listRef.current.scrollHeight);
  }, [chat?.messages]);

  // Запрет отправки (решение 07.09): ключ визави сменился, человек ещё не
  // решил. Правило считает КОНТРОЛЛЕР, страница только читает.
  const sendStopped = Boolean(chat?.sendBlocked);

  const send = () => {
    const text = input.trim();
    const draft = file;
    // 🔴 При запрете НЕ очищаем поле: молчаливая очистка съела бы текст.
    // Файл без текста — валидное сообщение (ATT С2), текст без файла — как был.
    // 🔴 `uploading` проверяется ЗДЕСЬ, а не только погашенной кнопкой:
    // Enter в поле шёл мимо неё, а загрузка вложения идёт ВНЕ очереди
    // контроллера — текст успевал уехать раньше сообщения с файлом, и
    // переписка вставала не в том порядке (ревью 12.09, р.5, Medium).
    if (sendStopped || Boolean(chat?.uploading)
        || (!text && !draft) || !controllerRef.current) return;
    const quote = replyTo;
    setInput('');
    setFile(null);
    setReplyTo(null);
    // 🔴 Отказ контроллера (`false`) — запрет выставился, пока клик летел,
    // или вложение не загрузилось: черновик (и текст, и файл) возвращается,
    // если человек не начал собирать новое (ревью WEB-14 р.1, Medium:
    // иначе текст исчезал вместе с очисткой).
    Promise.resolve(controllerRef.current.send(text, draft ? [draft] : [], quote))
      .then((sent) => {
        if (sent === false) {
          setInput((cur) => cur || text);
          if (draft) setFile((cur) => cur || draft);
          // Цитата возвращается вместе с текстом — и только если человек не
          // выбрал другую, пока летел отказ (тот же довод, что у файла).
          if (quote) setReplyTo((cur) => cur || quote);
        }
      })
      .catch(() => {});
  };

  const body = () => {
    if (!loaded) return <p className="msgChatNote">Загрузка…</p>;
    if (!chatEnabled) return <p className="msgChatNote">Чат недоступен.</p>;
    if (taking) {
      return <p className="msgChatNote" data-testid="chat-taking">Переносим чат сюда…</p>;
    }
    if (yielded) {
      // Осознанно НЕ пытаемся забрать лидерство обратно автоматически (ревью
      // Codex р.1, Medium): просивший мог не дождаться и уйти, и автозахват
      // стал бы ТРЕТЬЕЙ точкой перезапуска эффекта — ровно тем механизмом,
      // который вчера и подвёл. Выход у человека есть здесь, кнопкой.
      return (
        <p className="msgChatNote" data-testid="chat-yielded">
          Чат перенесён в другую вкладку — работайте там.
          {' '}
          <button type="button" onClick={takeover} disabled={taking}>Вернуть сюда</button>
        </p>
      );
    }
    if (blocked) {
      return (
        <p className="msgChatNote" data-testid="chat-blocked">
          {takeFailed
            ? 'Не удалось перенести чат: та вкладка не ответила. Попробуйте ещё раз или закройте её.'
            : 'Чат открыт в другой вкладке этого браузера.'}
          {canAskYield() && (
            <>
              {' '}
              <button type="button" onClick={takeover} disabled={taking}>
                Открыть чат здесь
              </button>
            </>
          )}
          {!canAskYield() && ' Работайте там или закройте её и обновите эту.'}
        </p>
      );
    }
    if (!chat || chat.phase === 'init') return <p className="msgChatNote">Подключение к защищённому чату…</p>;
    // Ожидание визави — не ошибка (он ещё не открывал чат): экран ждёт сам,
    // кнопка для нетерпеливых, красным НЕ красим.
    if (chat.phase === 'error' && chat.error?.kind === 'peer_no_device') {
      return (
        <p className="msgChatNote" data-testid="peer-waiting">
          {chat.error.text}
          {' '}
          <button type="button" onClick={retryNow}>Проверить сейчас</button>
        </p>
      );
    }
    // Собеседника удалили — это не поломка чата, а конец переписки: красным
    // НЕ красим и кнопку «проверить» не даём (проверять нечего). Лидерство к
    // этому моменту уже отпущено — соседние чаты открываются.
    // Д-20: чат открыт на ДРУГОМ устройстве. Не «нет доступа», а вопрос с
    // действием — как перенос между вкладками, который человек уже знает.
    if (chat.phase === 'error' && chat.error?.kind === 'other_device') {
      return (
        <p className="msgChatNote" data-testid="other-device">
          {chat.error.text}
          {' '}
          <button type="button" data-testid="other-device-take" onClick={takeDevice}>
            Перенести сюда
          </button>
        </p>
      );
    }
    /**
     * Д-3 «хвост»: крипто-стор погиб, did цел (чистка места в браузере сносит
     * именно IndexedDB). Копии `.totalchat` у владельца нет — она сделана для
     * участника, чей did заморожен member-JWT. Зато владельцу доступно то, чего
     * нет у участника: НОВЫЙ did. Это ровно та же ротация, что и в Д-20, и
     * поднимает чат она честно — теряются лишь конверты, зашифрованные для
     * пропавших ключей, а показанная переписка лежит в общей базе браузера.
     */
    if (chat.phase === 'error' && chat.error?.kind === 'device_identity_lost') {
      return (
        <p className="msgChatNote msgChatError" data-testid="identity-lost">
          {chat.error.text}
          {' Подключите этот браузер заново — переписка на экране останется,'
            + ' но сообщения, которые не успели прийти, не откроются.'}
          {/* 🔴 Ротация did меняет ключ устройства, значит у КАЖДОГО собеседника
              загорится «ключ сменился», а по правилу WEB-14 у него встанет
              отправка, пока он не нажмёт «Код сверен». Не сказать об этом —
              оставить человека гадать, почему ему перестали писать. */}
          {' Собеседникам придётся заново сверить код безопасности: до этого'
            + ' они не смогут вам писать.'}
          {' '}
          <button type="button" data-testid="identity-lost-rejoin" onClick={takeDevice}>
            Подключить заново
          </button>
        </p>
      );
    }
    if (chat.phase === 'error' && chat.error?.kind === 'peer_gone') {
      return (
        <p className="msgChatNote" data-testid="peer-gone">{chat.error.text}</p>
      );
    }
    if (chat.phase === 'error' && !chat.error?.retriable) {
      return <p className="msgChatNote msgChatError">{chat.error?.text ?? 'Чат не поднялся.'}</p>;
    }
    const changed = chat.trust?.status === 'key_changed' ? 1 : 0;
    return (
      <>
        {/* 🔴 WEB-23: `firstUse` сюда БОЛЬШЕ НЕ ПЕРЕДАЁТСЯ. «Первый контакт» —
            состояние по умолчанию (снимается только кнопкой «Код сверен»,
            которую в пилоте не нажимал никто), то есть строка висела у всех и
            всегда. Она переехала в панель «Защита переписки». А «ключ
            СМЕНИЛСЯ» остаётся здесь: это событие с ценой, на нём стоит запрет
            отправки WEB-14, и прятать его — отдать канал тому, кто ключ
            подменил. */}
        <PinnedWarnings
          changed={changed}
          onShow={() => listRef.current?.scrollTo(0, 0)}
        />
        {/* 🔴 Прокручивается ВСЯ надстройка вместе с лентой (см. msgChat.css,
            `.msgScroll`). Плашки доверия и код безопасности, оставаясь
            неподвижными, съедали экран телефона: в комнате живой замер 30.08
            дал шесть плашек по 113px при области 648. Прижат только ввод. */}
        <div
          className="msgScroll"
          ref={listRef}
          // 🔴 Прокручиваемая область обязана быть достижима с клавиатуры
          // (ревью Codex 30.08, Medium): без `tabIndex` до истории не
          // добраться ни стрелками, ни PageUp — Tab уходит сразу в ввод.
          tabIndex={0}
          role="region"
          aria-label="Переписка и предупреждения безопасности"
          data-testid="msg-scroll"
        >
        {/* WEB-23: полная плашка осталась ТОЛЬКО у смены ключа. Плашка
            «первый контакт» и код безопасности уехали в панель — там же, где
            кнопка «Код сверен» и своё устройство. */}
        {chat.trust?.status === 'key_changed' && (
          <div className="msgTrustBanner msgTrustDanger">
            <span>{TRUST_TEXT.key_changed}</span>
            <button type="button" onClick={() => controllerRef.current?.markVerified()}>
              Код сверен
            </button>
          </div>
        )}
        <ul className="msgList">
          {(chat.messages ?? []).map((m) => (
            <li
              key={m.messageId}
              data-message-id={m.messageId}
              // tabIndex -1: строка не встаёт в обход по Tab, но МОЖЕТ принять
              // фокус при переходе к оригиналу (WEB-16 срез 4)
              tabIndex={-1}
              data-testid={`msg-item-${m.messageId}`}
              className={`msgItem ${m.direction === 'out' ? 'msgOut' : 'msgIn'} ${m.pending ? 'msgPending' : ''}`
                + `${highlighted === m.messageId ? ' msgItemFound' : ''}`}
            >
              {/* WEB-16: цитата НАД текстом. Оригинал ищется только в ЭТОЙ
                  переписке (byId собран из её же ленты): поиск по одному id
                  где угодно показал бы текст из чужого чата. */}
              <ReplyQuote
                quote={m.replyTo}
                original={m.replyTo ? byId.get(m.replyTo.messageId) : null}
                names={{ peerName }}
                onJump={jumpTo}
              />
              <span className="msgText">
                {/* Сообщение-файл приходит с пустым body — это не «не
                    расшифровано», недоверие выражается null */}
                {m.text ?? (m.attachments?.length ? '' : 'Сообщение не удалось расшифровать')}
              </span>
              <MsgAttachments
                attachments={m.attachments}
                fetchBlob={(meta) => controllerRef.current.downloadAttachment(meta)}
              />
              <span className="msgMeta">
                {m.pending ? 'отправляется…' : m.sentAt ? new Date(m.sentAt).toLocaleString() : ''}
                {/* Отвечать можно и на своё, и на чужое — но не на то, что
                    ещё не ушло: у висящего в очереди сообщения собеседника
                    нет, и цитата вела бы в пустоту. */}
                {!m.pending && (
                  <ReplyButton
                    disabled={chat.phase !== 'ready'}
                    onClick={() => {
                      const snap = replySnapshot(
                        m, m.direction === 'out' ? '' : peerName,
                      );
                      // `mine` живёт только на этом экране — см. ReplyDraftBar
                      setReplyTo(snap && { ...snap, mine: m.direction === 'out' });
                    }}
                  />
                )}
              </span>
            </li>
          ))}
        </ul>
        </div>
        {chat.error && (
          <div className="msgChatError msgSendError">
            <span>{chat.error.text}</span>
            {chat.error.retriable && (
              <button type="button" onClick={() => controllerRef.current?.retryPending()}>
                Повторить
              </button>
            )}
          </div>
        )}
        <SendBlocked
          blocked={chat.sendBlocked}
          onSendAnyway={() => controllerRef.current?.sendAnyway()}
        />
        <AttachmentDraft file={file} onClear={() => setFile(null)} />
        <ReplyDraftBar quote={replyTo} onCancel={() => setReplyTo(null)} />
        <UploadingNote uploading={chat.uploading} />
        <div className="msgInputRow">
          {/* C2a (заявка владельца 11.09): скрепка — ВНУТРИ белого бокса
              ввода, справа, «как элемент этого блока». Рамка и фон бывшего
              поля теперь у бокса, фокус-кольцо — на :focus-within. */}
          <div className="msgInputBox" data-testid="input-box">
            <input
              type="text"
              ref={inputRef}
              value={input}
              placeholder="Сообщение…"
              disabled={chat.phase !== 'ready'}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            />
            {/* Скрытый input — скрепка живёт кнопкой: нативный контрол не
                стилизуется и ломал бы строку ввода */}
            <input
              type="file"
              ref={fileRef}
              hidden
              data-testid="attach-input"
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (picked) setFile(picked);
                // тот же файл можно выбрать повторно после ✕
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="msgAttachBtn"
              title="Прикрепить файл"
              aria-label="Прикрепить файл"
              disabled={chat.phase !== 'ready' || Boolean(chat.uploading)}
              onClick={() => fileRef.current?.click()}
            >
              📎
            </button>
            {/* WEB-31: отправка — ВНУТРИ бокса, справа от скрепки. Появляется,
                когда есть что отправлять; при запрете отправки и во время
                загрузки видна и погашена (правило — в самом компоненте). */}
            <SendButton
              inputRef={inputRef}
              text={input}
              file={file}
              ready={chat.phase === 'ready'}
              sendStopped={sendStopped}
              uploading={chat.uploading}
              onSend={send}
            />
          </div>
        </div>
      </>
    );
  };

  // WEB-23: своё устройство для панели. Отказ ручки НЕ ломает панель — она
  // показывает всё остальное; раздел «Это устройство» просто не появляется
  // (⭐ отказ чтения — это `null`, а не значение: грабля Д-17).
  useEffect(() => {
    // 🔴 `|| myDevice` — не оптимизация, а СТОП-КРАН: `myDevice` стоит в
    // зависимостях, и без него каждый успешный ответ перезаводил бы эффект —
    // запросы шли бы кругом, пока панель открыта (ревью 03.09).
    if (!securityOpen || myDevice) return undefined;
    let alive = true;
    new MsgApi(browserDeviceId()).myDevice()
      .then((info) => { if (alive) setMyDevice(info ?? null); })
      // 🔴 `warn`, а не `debug`: сюда приходят и 401/403 — ровно те коды,
      // которыми Д-20 отличает «устройство ревокнули». Глухое проглатывание
      // отказа лишило бы разбор боевого дня единственного следа (ревью 03.09).
      .catch((e) => logger.warn('Своё устройство для панели защиты не приехало', e));
    return () => { alive = false; };
  }, [securityOpen, myDevice]);

  // 🔴 Два падежа, а не один: заголовок читается «Чат с участником #7», а
  // строка в панели — «Участник #7». Слепив их в одну переменную, я сломал
  // заголовок на «Чат с участник #7» (поймали пины).
  const peerTitle = isMember
    ? `Чат с ${chat?.peer?.name ?? `участником #${memberId}`}`
    : `Чат с ${peerFio ?? `сотрудником #${userId}`}`;
  const peerName = isMember
    ? (chat?.peer?.name ?? `Участник #${memberId}`)
    : (peerFio ?? `Сотрудник #${userId}`);

  // WEB-23: в 1:1 собеседник один, значит и строка в панели одна.
  // 🔴 Только при ЖИВОМ чате (ревью 03.09, Medium). Замок и панель висят
  // снаружи `body()`, а `chat` после уступки вкладки и ухода собеседника не
  // обнуляется: без этой проверки в шапке горела бы цифра, а в панели стояла
  // бы кнопка «Код сверен», зовущая `controllerRef.current`, который уже
  // `null`. Это дословно тот дефект, который ревью 30.08 закрыло у строки
  // доверия: сводка не имеет права жить дольше того, что она сводит.
  const chatLive = chat?.phase === 'ready' && !gone && !yielded && !blocked;
  const securityPeople = chatLive && chat?.trust
    ? [{ key: 'peer', name: peerName, status: chat.trust.status }]
    : [];

  // WEB-10: страница чата живёт в ТОМ ЖЕ каркасе, что и остальной кабинет —
  // с бургером, шапкой и тумблером темы. Раньше у неё был свой корень с
  // собственным `aside` и `height: 100vh`, и на телефоне колонки вставали
  // рядом: переписке оставалась треть экрана, а темы на этой странице не было
  // вовсе (её переключатель живёт в шапке каркаса).
  return (
    <CabinetShell
      title={peerTitle}
      floor={false}
      fill
      action={(
        <SecurityLock
          count={unverifiedCount(securityPeople)}
          expanded={securityOpen}
          lockRef={securityLockRef}
          onOpen={() => setSecurityOpen((v) => !v)}
        />
      )}
    >
      <div className="MsgChat">
        <div className="msgChatMain">
          {/* WEB-23: строка «🔒 E2E — сервер видит только запечатанные
              конверты» отсюда УБРАНА. Она стоила целой строки над лентой на
              каждом экране переписки и пересказывала то, что человек уже
              прочитал на лендинге и в памятке. Признак защиты остался —
              замок в шапке каркаса, он же вход сюда. */}
          {securityOpen && (
            <SecurityPanel
              people={securityPeople}
              safetyNumber={chatLive ? (chat?.safetyNumber ?? null) : null}
              peerWord={isMember ? 'участника' : 'сотрудника'}
              myDevice={myDevice}
              onVerify={() => controllerRef.current?.markVerified()}
              onClose={closeSecurity}
            />
          )}
          {body()}
        </div>
      </div>
    </CabinetShell>
  );
};

export default MsgChat;
